import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';

import { OrderFormCategoryDTO } from 'support/types/dtos';

import { useActiveElement } from './hooks/useActiveElement';

import { EmptySearchResultsSidebar } from './EmptySearchResultsSidebar';

type CategoriesProps = {
  withoutMatches?: boolean;
  categories: Array<OrderFormCategoryDTO>;
};

export const Categories = ({ withoutMatches, categories }: CategoriesProps) => {
  const { t } = useTranslation();
  const validCategories = categories.filter((category) => category.name);
  const categoryIds = validCategories.map((category) => category.name as string);
  const activeElement = useActiveElement(categoryIds);

  const navRef = useRef<HTMLElement>(null);

  const [clickedCategory, setClickedCategory] = useState<string | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetClickedCategory = useCallback(() => {
    setClickedCategory(null);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, []);

  const debouncedResetClickedCategory = useDebounceCallback(resetClickedCategory, 150);

  useEffect(() => {
    const handleScroll = () => {
      if (clickedCategory) {
        debouncedResetClickedCategory();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [clickedCategory, debouncedResetClickedCategory]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();

      try {
        const targetId = event.currentTarget.getAttribute('href')?.substring(1);
        if (!targetId) {
          return;
        }

        setClickedCategory(targetId);

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          resetClickedCategory();
        }, 1500);

        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });

          targetElement.focus();
        }
      } catch (error) {
        console.warn('Error scrolling to category:', error);
      }
    },
    [resetClickedCategory],
  );

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }, []);

  if (validCategories.length === 0 && !withoutMatches) {
    return null;
  }

  return (
    <nav ref={navRef} role="navigation" aria-label="Order form categories">
      <h2 className="p-2 text-xs font-medium leading-none tracking-wide text-gray-500" id="categories-heading">
        {t('orderForms:form.sidebar.categories.title')}
      </h2>
      {withoutMatches ? (
        <EmptySearchResultsSidebar />
      ) : (
        <ul role="list" aria-labelledby="categories-heading">
          {validCategories.map((category) => {
            const isActive = (clickedCategory || activeElement) === category.name;
            return (
              <li key={category.name} role="listitem">
                <a
                  className={`block w-full rounded-md p-2 text-sm font-medium transition-colors focus:outline-none ${
                    isActive ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                  href={`#${category.name}`}
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                  aria-current={isActive ? 'location' : undefined}
                  tabIndex={0}
                >
                  {category.name}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
};
